import { useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchConversationsPage,
    type ConversationRow,
    type ConversationsParams
} from '../services/conversations';
import { CONVERSATIONS_INFINITE_KEY } from './queryKeys';
import { invalidateSidebarCounts } from './useSidebarCounts';

type InfiniteParams = Omit<ConversationsParams, 'page'> & { enabled?: boolean };

export function useInfiniteConversations(params: InfiniteParams) {
    const queryClient = useQueryClient();

    const queryKey = [
        CONVERSATIONS_INFINITE_KEY,
        params.filter,
        params.limit,
        params.sort,
        params.order,
        params.team?.join(',') ?? '',
        params.assignee?.join(',') ?? '',
        params.practiceIds?.join(',') ?? '',
        params.status?.join(',') ?? '',
        params.search ?? ''
    ];

    const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useInfiniteQuery({
            queryKey,
            queryFn: ({ pageParam }) => fetchConversationsPage({ ...params, page: pageParam }),
            initialPageParam: 1,
            getNextPageParam: (lastPage) => {
                if (lastPage.meta.page < lastPage.meta.totalPages) return lastPage.meta.page + 1;
                return undefined;
            },
            staleTime: 60 * 1000,
            refetchOnWindowFocus: true,
            refetchOnMount: 'always',
            enabled: params.enabled !== false
        });

    const conversations: ConversationRow[] = data?.pages.flatMap((p) => p.data) ?? [];
    const total = data?.pages[0]?.meta.total ?? 0;

    const refresh = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [CONVERSATIONS_INFINITE_KEY] }),
            invalidateSidebarCounts(queryClient)
        ]);
    }, [queryClient]);

    return {
        conversations,
        total,
        isLoading,
        isFetching,
        isError,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
        isFetchingNextPage,
        refresh
    };
}
